import React, { FunctionComponent } from 'react';
import styled, { css, FlattenSimpleInterpolation } from 'styled-components';
import { MediaData } from '~admin/model/media-data';
import { MediaPreviewImage } from '~admin/view/components/ui/form/fields/media/media-preview/media-preview-image.component';
import { MediaPreviewVideo } from '~admin/view/components/ui/form/fields/media/media-preview/media-preview-video.component';
import { MediaType } from '~client/model/media-type';
import { Box } from '~common/components/box.component';
import { Column } from '~common/components/column.component';
import { Span } from '~common/components/span.component';
import { CancelIcon } from '~web/view/components/ui/icon/cancel.icon';
import { PlusIcon } from '~web/view/components/ui/icon/plus.icon';

interface SwitchProps {
  media: MediaData;
  mediaType: MediaType;
}

const MediaPreviewSwitch: React.FunctionComponent<SwitchProps> = ({ media, mediaType }) => {
  switch (mediaType) {
    case MediaType.IMAGE:
    case MediaType.SEATMAP:
      return <MediaPreviewImage media={media} />;
    case MediaType.VIDEO:
      return <MediaPreviewVideo media={media} />;
    default:
      throw Error(`${mediaType} is not supported by the media picker.`);
  }
};

interface MediaPreviewInnerProps {
  media: MediaData;
  mediaType: MediaType;
  disabled?: boolean;
}

const MediaPreviewInner: FunctionComponent<MediaPreviewInnerProps> = ({
  media,
  mediaType,
  disabled,
}) => {
  if (media) {
    return <MediaPreviewSwitch mediaType={mediaType} media={media} />;
  }

  return disabled ? <CancelIcon /> : <PlusIcon width={24} height={24} />;
};

interface MediaPreviewProps {
  label: string;
  media: MediaData;
  mediaType: MediaType;
  error: boolean;
  onPreviewClick?: VoidFunction;
  disabled?: boolean;
}

export const MediaPreview: FunctionComponent<MediaPreviewProps> = ({
  label,
  media,
  mediaType,
  error,
  onPreviewClick,
  disabled,
}) => {
  return (
    <Container disabled={disabled}>
      <Label error={error}>{label}</Label>
      <Preview
        error={error}
        disabled={disabled}
        onClick={(): void => {
          if (!disabled) {
            onPreviewClick();
          }
        }}
      >
        <MediaPreviewInner media={media} mediaType={mediaType} disabled={disabled} />
      </Preview>
    </Container>
  );
};

const Container = styled(({ disabled, ...rest }) => <Column {...rest} />)<{
  disabled: boolean;
}>`
  align-items: center;

  ${(props): FlattenSimpleInterpolation | null =>
    props.disabled &&
    css`
      opacity: 0.5;
    `}
`;

const Label = styled(({ error, ...rest }) => <Span {...rest} />)<{
  error: boolean;
}>`
  font-weight: bold;
  font-size: 16px;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: ${(props): string => (props.error ? props.theme.colors.red : props.theme.colors.gray.v1)};
  margin-bottom: 4px;
`;

const Preview = styled(({ error, disabled, ...rest }) => <Box {...rest} />)<{
  error: boolean;
  disabled: boolean;
}>`
  height: 170px;
  width: 170px;
  border: 2px solid
    ${(props): string => (props.error ? props.theme.colors.red : props.theme.colors.gray.v3)};
  border-radius: 16px;
  overflow: hidden;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  ${(props): FlattenSimpleInterpolation | null =>
    props.disabled &&
    css`
      cursor: not-allowed;
    `}
`;



// WEBPACK FOOTER //
// ./view/components/ui/form/fields/media/media-preview/media-preview.component.tsx